import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanLoad,
  CanActivate,
  Route,
  Router,
  Params,
} from '@angular/router';
import { AuthService } from '../../core/abstraction/auth.service';
import { USER_TYPE, User } from '../../core/models/User';
import { AuthenticationService } from '../services/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private authentication: AuthenticationService,
    private router: Router,
  ) {}

  // If not logged, should redirect to login/register screen
  checkAuthentication(path: string, queryParams?: Params): boolean {
    const subpaths = path.split('/');
    if (!this.authService.hasConfirmedMail()) {
      // Allow signin
      if (this.authService.loggedUser && !this.authService.loggedUser?.flags.mailConfirmed) {
        if (
          subpaths.length >= 3 &&
          subpaths[2] &&
          (subpaths[2] === 'signin' || subpaths[2].startsWith('signin'))
        ) {
          return false;
        }
        if ((subpaths.length === 3 || subpaths.length === 4) && subpaths[2] === 'register') {
          return false;
        }
        if ((subpaths.length === 3 || subpaths.length === 4) && subpaths[2] === 'confirm-mail') {
          return true;
        }
        this.router.navigate(['confirm-mail'], {
          queryParams: { rerouteUrl: path },
        });
      } else {
        if (
          subpaths.length >= 3 &&
          subpaths[2] &&
          (subpaths[2] === 'signin' || subpaths[2].startsWith('signin'))
        ) {
          return true;
        }
        if ((subpaths.length === 3 || subpaths.length === 4) && subpaths[2].includes('register')) {
          return true;
        }
        this.router.navigate(['auth', 'signin'], {
          queryParams: { rerouteUrl: path },
        });
        return;
      }
    } else if (this.authService.needToCompletedOnboarding() || this.authService.hasToUpdateWork()) {
      // If logged, but missing fields should redirect to registry step
      if (!this.authService.loggedUser?.cuit) {
        if (subpaths.length === 4 && subpaths[3] === 'id') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'id']);
      } else if (this.missingDataConfirmStep(this.authService.loggedUser)) {
        if (subpaths.length === 4 && subpaths[3] === 'personal-data') {
          return true;
        }
        if (this.authService.loggedUser.type !== USER_TYPE.WHITELABEL)
          this.router.navigate(['auth', 'signup', 'personal-data']);
        // } else if (this.authService.loggedUser.data.isPEP) {
        //   if (subpaths.length === 4 && subpaths[3] === 'pep-info') {
        //     return true;
        //   }
        //   this.router.navigate(['auth', 'signup', 'pep-info']);
      } else if (this.authService.hasToUploadPepDocuments()) {
        if (subpaths.length === 4 && subpaths[3] === 'pep-info') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'pep-info']);
      } else if (this.authService.hasToUpdateWork()) {
        if (subpaths.length === 4 && subpaths[3] === 'data-extra') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'data-extra']);
      } else if (!this.authService.loggedUser.bankAccounts.ARS.length) {
        if (subpaths.length === 4 && subpaths[3] === 'bank-info') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'bank-info']);
      } else if (this.authService.hasToUploadCompanyDocuments()) {
        if (subpaths.length === 4 && subpaths[3] === 'documents') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'documents']);
      } else if (this.authService.haveToOuploadDNIS() && !this.authService.isCompany()) {
        if (subpaths.length === 4 && subpaths[3] === 'id-photo') {
          return true;
        }
        this.router.navigate(['auth', 'signup', 'id-photo']);
      } else if (subpaths.length === 3 && subpaths[2] === 'signin') {
        this.router.navigate(['core']);
        return true;
      } else {
        // If you reached here, the user has everything and is logged so send them to
        // Dashboard if he's still in auth
        if (subpaths.length > 2 && subpaths[1] === 'auth') {
          this.router.navigate(['core']);
        }
        return true;
      }
    } else if (queryParams?.platform && queryParams?.token) {
      if (queryParams.platform === 'vtex') this.authentication.loginVtex(queryParams.token);
    } else {
      this.router.navigate(['core']);
    }
  }

  canLoad(route: Route): boolean {
    return this.checkAuthentication(route.path); // No carga el route entero
  }

  canActivate(activatedRouter: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): boolean {
    return this.checkAuthentication(routerState.url, activatedRouter.queryParams);
  }

  missingImageUploadStep(user: User): boolean {
    return !user.passportCheck || !user.passportCheck[0].present || !user.passportCheck[1].present;
  }

  missingDataConfirmStep(user: User): boolean {
    if (this.authService.isCompany()) {
      return !user.phoneNumber || !user.data?.postalCode || this.authentication.reverseRegister;
    } else {
      return (
        !user.phoneNumber ||
        !user.data?.birthDate ||
        !user.data?.civilState ||
        !user.data?.postalCode ||
        user.data.isPEP === undefined ||
        this.authentication.reverseRegister
      );
    }
  }

  missingDataExtraStep(user: User): boolean {
    return !user.data.IVA || !user.under10kUSDDeclarationSigned || !user.phoneNumber;
  }

  missingFundingStep(user: User): boolean {
    return user.investorExperience === undefined;
  }
}
